import React from 'react';
import { KPIDirection, KPIHistoryPoint, KPIPeriodicity } from '../types';
import { computeHistoryDeltas, formatPeriodLabel, formatValue, nextPeriodLabel, periodDateLabel } from '../helpers';
import { Plus, Trash2 } from 'lucide-react';

interface Props {
    periodicity: KPIPeriodicity;
    direction: KPIDirection;
    unit: string;
    history: KPIHistoryPoint[];
    onChange: (history: KPIHistoryPoint[]) => void;
    canEdit: boolean;
}

/**
 * Editable periodic data-entry table: one row per period (day/week/month).
 * Change (Δ) and variation (%) are derived live from consecutive values —
 * never stored, always recomputed from the entered numbers.
 */
const KPIHistoryTable: React.FC<Props> = ({ periodicity, direction, unit, history, onChange, canEdit }) => {
    const rows = computeHistoryDeltas(history);
    const lastLabel = history.length > 0 ? history[history.length - 1].date : null;
    const currentPeriodLabel = periodDateLabel(periodicity);
    const alreadyLoggedCurrent = lastLabel === currentPeriodLabel;
    const nextLabel = nextPeriodLabel(periodicity, lastLabel);

    const updateValue = (idx: number, raw: string) => {
        const value = parseFloat(raw);
        const next = history.map((h, i) => (i === idx ? { ...h, value: isNaN(value) ? 0 : value } : h));
        onChange(next);
    };

    const removeRow = (idx: number) => {
        onChange(history.filter((_, i) => i !== idx));
    };

    const addPeriod = () => {
        const label = alreadyLoggedCurrent ? nextLabel : currentPeriodLabel;
        const lastValue = history.length > 0 ? history[history.length - 1].value : 0;
        onChange([...history, { date: label, value: lastValue }]);
    };

    const trendCls = (change: number | null) => {
        if (change === null || change === 0) return 'text-gray-400';
        const good = (direction === 'higher-is-better' && change > 0) || (direction === 'lower-is-better' && change < 0);
        return good ? 'text-green-600' : 'text-red-500';
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-gray-600">Historique des valeurs</label>
                {canEdit && (
                    <button type="button" onClick={addPeriod} className="flex items-center gap-1 text-xs text-toola-500 hover:text-toola-600 font-medium">
                        <Plus size={12} /> Ajouter {formatPeriodLabel(alreadyLoggedCurrent ? nextLabel : currentPeriodLabel, periodicity)}
                    </button>
                )}
            </div>
            <div className="rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="bg-gray-50 text-gray-500">
                            <th className="text-left font-medium px-2.5 py-1.5">Période</th>
                            <th className="text-right font-medium px-2.5 py-1.5">Valeur</th>
                            <th className="text-right font-medium px-2.5 py-1.5">Changement</th>
                            <th className="text-right font-medium px-2.5 py-1.5">Variation</th>
                            {canEdit && <th className="w-7 px-1"></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr key={row.date + i} className="border-t border-gray-100">
                                <td className="px-2.5 py-1.5 text-gray-600 whitespace-nowrap">{formatPeriodLabel(row.date, periodicity)}</td>
                                <td className="px-2.5 py-1 text-right">
                                    <input
                                        type="number"
                                        value={row.value}
                                        disabled={!canEdit}
                                        onChange={(e) => updateValue(i, e.target.value)}
                                        className="w-20 text-right rounded-lg border border-gray-200 px-1.5 py-1 text-xs outline-none focus:border-toola-500 disabled:bg-gray-50"
                                    />
                                </td>
                                <td className={`px-2.5 py-1.5 text-right font-medium ${trendCls(row.change)}`}>
                                    {row.change === null ? '—' : `${row.change > 0 ? '+' : ''}${formatValue(row.change, unit)}`}
                                </td>
                                <td className={`px-2.5 py-1.5 text-right font-medium ${trendCls(row.change)}`}>
                                    {row.variationPct === null ? '—' : `${row.variationPct > 0 ? '+' : ''}${row.variationPct}%`}
                                </td>
                                {canEdit && (
                                    <td className="px-1 text-center">
                                        <button type="button" onClick={() => removeRow(i)} className="text-gray-300 hover:text-red-400">
                                            <Trash2 size={12} />
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={canEdit ? 5 : 4} className="px-2.5 py-3 text-center text-gray-400">
                                    Aucune donnée — ajoutez une première période
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default KPIHistoryTable;
